import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { ActionablePlaceholder } from 'ui-components/components/display-grid/actionable-placeholder';

import { useQuickSearchTerm } from '../../../../hooks/routing/use-quick-search-term';
import EmptyBoxSvg from '../../../../svgs/empty-box.svg';
import { id as orderNumberId } from '../../../../filters/options/order-number';
import { id as recipentNameId } from '../../../../filters/options/recipient-name';
import { id as itemSkuId } from '../../../../filters/options/item-sku';
import { MessageDescriptorPropType } from '../../../../prop-types/intl';
import { quickSearchFilterIdParam, quickSearchFilterValueParam } from '../../../../advanced-search/hooks/index';
import { shouldOpenAdvancedSearchFlag } from '../../../../advanced-search/orders/constants';

const messages = defineMessages({
  title: {
    description: 'description header displaying that no results matched within the past 2 years ',
    defaultMessage: 'Quicksearch did not find any results within the past 2 years.',
    id: 'Zona50',
  },
  description: {
    description: 'description for the quicksearch placeholder ',
    defaultMessage: 'Try advanced search using {orderNumberLink}, {recipientNameLink}, {itemSkuLink}, or {otherCriteriaLink}',
    id: 'K8wut+',
  },
  orderNumberLink: {
    description: 'orderNumberLink for the quicksearch placeholder ',
    defaultMessage: 'Order #',
    id: 'Uvr/hd',
  },
  recipientNameLink: {
    description: 'recipientNameLink for the quicksearch placeholder ',
    defaultMessage: 'Recipient Name',
    id: 'SmpvVX',
  },
  itemSkuLink: {
    description: 'itemSkuLink for the quicksearch placeholder ',
    defaultMessage: 'Item SKU',
    id: 'YHEBDU',
  },
  otherCriteriaLink: {
    description: 'otherCriteriaLink for the quicksearch placeholder ',
    defaultMessage: 'other criteria',
    id: 'ak4ohy',
  },
  altText: {
    description: 'alternate text for image tag',
    defaultMessage: 'Empty Box',
    id: 'QAmSDz',
  },
});

const AdvancedSearchLink = ({ message, filterId }) => {
  const quickSearchTerm = useQuickSearchTerm();
  const quickSearchParams = `&${quickSearchFilterIdParam}=${filterId}&${quickSearchFilterValueParam}=${quickSearchTerm}`;
  return (
    <Link
      to={{
        pathname: '/orders/advanced-search',
        search: `?page=1&pageSize=250${quickSearchParams}`,
      }}
    >
      <FormattedMessage {...message} />
    </Link>
  );
};

AdvancedSearchLink.propTypes = {
  message: MessageDescriptorPropType.isRequired,
  filterId: PropTypes.string,
};

// We do not want to send the advancedSearch request when opening it from other criteria.
//   Instead of sending search params, we set a flag to be used by the left nav container to open the advanced search form.
const OtherCriteriaLink = ({ setDescriptionVisible }) => {
  const quickSearchTerm = useQuickSearchTerm();
  const quickSearchParams = `&quickSearch=${quickSearchTerm}&${shouldOpenAdvancedSearchFlag}=true`;
  return (
    <Link
      onClick={() => setDescriptionVisible(false)}
      to={{
        search: `?page=1&pageSize=250${quickSearchParams}`,
      }}
    >
      <FormattedMessage {...messages.otherCriteriaLink} />
    </Link>
  );
};

OtherCriteriaLink.propTypes = {
  setDescriptionVisible: PropTypes.func.isRequired,
};

const values = {
  orderNumberLink: <AdvancedSearchLink filterId={orderNumberId} message={messages.orderNumberLink} />,
  recipientNameLink: <AdvancedSearchLink filterId={recipentNameId} message={messages.recipientNameLink} />,
  itemSkuLink: <AdvancedSearchLink filterId={itemSkuId} message={messages.itemSkuLink} />,
};

const QuicksearchPlaceholder = () => {
  const intl = useIntl();
  const [showDescription, setDescriptionVisible] = useState(true);
  const altText = intl.formatMessage(messages.altText);

  return (
    <ActionablePlaceholder
      altText={altText}
      description={
        showDescription && (
          <FormattedMessage
            {...messages.description}
            values={{
              ...values,
              otherCriteriaLink: <OtherCriteriaLink setDescriptionVisible={setDescriptionVisible} />,
            }}
          />
        )
      }
      svg={EmptyBoxSvg}
      title={<FormattedMessage {...messages.title} />}
    />
  );
};

export default QuicksearchPlaceholder;
